/**
 * Centralized logging utility with debug levels
 * Provides consistent logging across the application with development and production modes
 */

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Logger configuration interface
 */
interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
  prefix: string;
}

/**
 * Log level priority mapping - higher number = higher priority
 */
const logLevelPriority: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

/**
 * Default logger configuration based on environment
 * In development: all levels enabled
 * In production: only WARN and ERROR unless VITE_DEBUG is true
 */
const defaultConfig: LoggerConfig = {
  enabled: import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true',
  minLevel: import.meta.env.PROD ? 'WARN' : 'DEBUG',
  prefix: '[WALRUS]',
};

/**
 * Logger instance class
 * Provides structured logging with various levels and formatting
 */
class Logger {
  private config: LoggerConfig;
  private groupStack: string[] = [];

  constructor(config?: Partial<LoggerConfig>) {
    this.config = { ...defaultConfig, ...config };
    console.log(
      `${this.config.prefix} Logger initialized - enabled: ${this.config.enabled}, minLevel: ${this.config.minLevel}`
    );
  }

  /**
   * Check if a log level should be output based on current configuration
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return logLevelPriority[level] >= logLevelPriority[this.config.minLevel];
  }

  /**
   * Format log message with level, prefix, and optional timestamp
   */
  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = import.meta.env.DEV ? new Date().toISOString() : '';
    const indent = this.groupStack.length > 0 ? '  '.repeat(this.groupStack.length) : '';
    const levelPad = level.padEnd(5);

    if (timestamp) {
      return `${indent}${this.config.prefix}[${levelPad}][${timestamp}] ${message}`;
    }
    return `${indent}${this.config.prefix}[${levelPad}] ${message}`;
  }

  /**
   * Debug level logging - most verbose, typically disabled in production
   * @param message Log message
   * @param data Optional additional data to log
   */
  debug(message: string, ...data: unknown[]): void {
    if (!this.shouldLog('DEBUG')) return;
    const formatted = this.formatMessage('DEBUG', message);
    console.log(formatted, ...data);
  }

  /**
   * Info level logging - general informational messages
   * @param message Log message
   * @param data Optional additional data to log
   */
  info(message: string, ...data: unknown[]): void {
    if (!this.shouldLog('INFO')) return;
    const formatted = this.formatMessage('INFO', message);
    console.info(formatted, ...data);
  }

  /**
   * Warning level logging - potentially problematic situations
   * @param message Log message
   * @param data Optional additional data to log
   */
  warn(message: string, ...data: unknown[]): void {
    if (!this.shouldLog('WARN')) return;
    const formatted = this.formatMessage('WARN', message);
    console.warn(formatted, ...data);
  }

  /**
   * Error level logging - error conditions and exceptions
   * @param message Log message
   * @param data Optional additional data to log
   */
  error(message: string, ...data: unknown[]): void {
    if (!this.shouldLog('ERROR')) return;
    const formatted = this.formatMessage('ERROR', message);
    console.error(formatted, ...data);
  }

  /**
   * Start a grouped log section for better organization
   * @param label Group label to display
   */
  group(label: string): void {
    if (!this.config.enabled) return;
    this.groupStack.push(label);
    const formatted = this.formatMessage('INFO', `GROUP: ${label}`);
    console.group(formatted);
  }

  /**
   * End the current grouped log section
   */
  groupEnd(): void {
    if (!this.config.enabled) return;
    this.groupStack.pop();
    console.groupEnd();
  }

  /**
   * Start a timer for performance measurement
   * @param label Timer identifier/label
   */
  time(label: string): void {
    if (!this.config.enabled) return;
    console.time(`${this.config.prefix}[TIMER] ${label}`);
  }

  /**
   * End a timer and log the elapsed time
   * @param label Timer identifier/label
   */
  timeEnd(label: string): void {
    if (!this.config.enabled) return;
    console.timeEnd(`${this.config.prefix}[TIMER] ${label}`);
  }

  /**
   * Update logger configuration
   * @param config Partial configuration to merge with current config
   */
  setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.debug(`Logger config updated`, this.config);
  }

  /**
   * Get current logger configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

/**
 * Global logger instance - singleton pattern
 * Import and use this instance throughout the application
 */
export const logger = new Logger();

/**
 * Export types for use in other modules
 */
export type { LogLevel, LoggerConfig };
export { Logger };
